import React, { useState, useMemo } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import 'chart.js/auto';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardContent,
  Divider,
  Typography,
  CircularProgress,
  Tooltip,
  TextField,
  MenuItem,
  ButtonBase,
  Box
} from '@material-ui/core';
import ShowChartIcon from '@material-ui/icons/ShowChart';
import BarChartIcon from '@material-ui/icons/BarChart';
import TimelineIcon from '@material-ui/icons/Timeline';
import CheckIcon from '@material-ui/icons/Check';

const METRICS = [
  { key: 'totalRequested', label: 'Requested', color: '#43A047', path: m => m?.totalRequested?.chartData, total: m => m?.totalRequested?.totalCount || 0 },
  { key: 'totalDelivered', label: 'Delivered', color: '#496DDB', path: m => m?.totalDelivered?.chartData, total: m => m?.totalDelivered?.totalCount || 0 },
  { key: 'totalOpened', label: 'Opened', color: '#FFC800', path: m => m?.totalOpened?.chartData, total: m => m?.totalOpened?.totalCount || 0 },
  { key: 'totalClicked', label: 'Clicked', color: '#443742', path: m => m?.totalClicked?.chartData, total: m => m?.totalClicked?.totalCount || 0 },
];

const CHART_TYPES = [
  { value: 'line', label: 'Line', Icon: ShowChartIcon },
  { value: 'area', label: 'Area', Icon: TimelineIcon },
  { value: 'bar', label: 'Bar', Icon: BarChartIcon },
];

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const useStyles = makeStyles((theme) => ({
  root: {},
  headerBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2)
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  chartTypeSelect: {
    width: 180,
    '& .MuiOutlinedInput-root': {
      height: 36,
      backgroundColor: theme.palette.common.white
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: '8px',
      paddingBottom: '8px',
      fontSize: '0.875rem'
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 10px) scale(1)'
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)'
    }
  },
  metricPanel: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  metricLegendLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5)
  },
  metricGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  metricOption: {
    padding: theme.spacing(0.75, 1.125),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    background: theme.palette.common.white,
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, transform 0.2s ease',
    minWidth: 0,
    '&:hover': {
      borderColor: theme.palette.text.secondary,
      boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
      transform: 'translateY(-1px)'
    }
  },
  metricOptionActive: {
    background: '#F7FAFF',
    boxShadow: '0 6px 18px rgba(73, 109, 219, 0.10)'
  },
  metricOptionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    whiteSpace: 'nowrap'
  },
  metricMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75)
  },
  metricColor: {
    width: 9,
    height: 9,
    borderRadius: '50%',
    flexShrink: 0
  },
  metricState: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 800,
    border: '1px solid currentColor',
    flexShrink: 0
  },
  metricStateIcon: {
    fontSize: 13
  },
  metricText: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.2
  },
  chartContainer: {
    position: 'relative',
    minHeight: 320,
    overflow: 'visible'
  },
  message: {
    textAlign: 'center',
    marginTop: theme.spacing(6)
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(8)
  },
  doughnutWrap: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
    minHeight: 440,
    overflow: 'visible'
  },
  doughnutGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(4),
    justifyItems: 'stretch',
    width: '100%',
    maxWidth: '100%',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  doughnutCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%'
  },
  doughnutTitle: {
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'center',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  doughnutChart: {
    width: 280,
    height: 280,
    padding: theme.spacing(2),
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
    '& canvas': {
      maxWidth: '100% !important',
      maxHeight: '100% !important',
      overflow: 'visible !important'
    }
  },
  doughnutStats: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: 12
  },
  doughnutPercentage: {
    fontSize: 20,
    fontWeight: 700,
    color: theme.palette.primary.main
  },
  doughnutLegend: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5)
  },
  legendRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: 13
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    flexShrink: 0
  }
}));

const EmailCampaignPerformanceGraph = props => {
  const { className, report, ...rest } = props;
  const { reportData, isLoading } = report;
  const classes = useStyles();

  const [chartType, setChartType] = useState('line');
  const [activeMetrics, setActiveMetrics] = useState(
    METRICS.map(m => m.key)
  );

  const hasData = reportData?.type === 'EMAIL' && reportData?.labels?.length > 0;

  const toggleMetric = key => {
    setActiveMetrics(prev =>
      prev.includes(key)
        ? prev.length > 1 ? prev.filter(k => k !== key) : prev
        : [...prev, key]
    );
  };

  const visibleMetrics = useMemo(
    () => METRICS.filter(m => activeMetrics.includes(m.key)),
    [activeMetrics]
  );

  const isDoughnut = chartType === 'doughnut';
  const isArea = chartType === 'area';

  const doughnutMetrics = useMemo(() => {
    if (!hasData || !isDoughnut) return [];
    const tm = reportData.totalMetrics;
    return [
      {
        title: 'Delivered',
        positive: tm?.totalDelivered?.totalCount || 0,
        total: tm?.totalRequested?.totalCount || 0,
        positiveLabel: 'Delivered',
        totalLabel: 'Requested',
        label: 'Delivered',
        notLabel: 'Not Delivered',
        color: '#496DDB'
      },
      {
        title: 'Opened',
        positive: tm?.totalOpened?.totalCount || 0,
        total: tm?.totalDelivered?.totalCount || 0,
        positiveLabel: 'Opened',
        totalLabel: 'Delivered',
        label: 'Opened',
        notLabel: 'Not Opened',
        color: '#FFC800'
      },
      {
        title: 'Clicked',
        positive: tm?.totalClicked?.totalCount || 0,
        total: tm?.totalOpened?.totalCount || 0,
        positiveLabel: 'Clicked',
        totalLabel: 'Opened',
        label: 'Clicked',
        notLabel: 'Not Clicked',
        color: '#443742'
      }
    ];
  }, [hasData, isDoughnut, reportData]);

  const createDoughnutChartData = metric => ({
    labels: [metric.label, metric.notLabel],
    datasets: [{
      data: [metric.positive, Math.max(0, metric.total - metric.positive)],
      backgroundColor: [metric.color, '#E0E0E0'],
      borderColor: ['#fff', '#fff'],
      borderWidth: 2,
      hoverOffset: 0
    }]
  });

  const chartData = useMemo(() => {
    if (!hasData) return null;
    const tm = reportData.totalMetrics;
    if (isDoughnut) {
      return null; // Will use doughnutMetrics instead
    }
    return {
      labels: reportData.labels,
      datasets: (visibleMetrics || []).map(m => ({
        label: m.label,
        data: m.path(tm) || [],
        borderColor: m.color,
        backgroundColor: isArea ? hexToRgba(m.color, 0.15) : hexToRgba(m.color, 0.7),
        fill: isArea,
        tension: 0.35,
        pointRadius: 3,
        pointHoverRadius: 6,
        borderWidth: 2
      }))
    };
  }, [hasData, reportData, visibleMetrics, chartType, isDoughnut, isArea]);

  const chartOptions = useMemo(() => {
    if (isDoughnut) {
      return {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => {
                const datasetLength = ctx.chart.data.datasets[0].data.length;
                const sum = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                const value = ctx.raw;
                const percentage = sum > 0 ? ((value / sum) * 100).toFixed(1) : 0;
                return ` ${ctx.label}: ${new Intl.NumberFormat().format(value)} (${percentage}%)`;
              }
            }
          }
        }
      };
    }
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: ctx => ` ${ctx.dataset.label}: ${new Intl.NumberFormat().format(ctx.raw)}`
          }
        }
      },
      scales: {
        x: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { maxTicksLimit: 14 } },
        y: { type: 'linear', grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { callback: v => new Intl.NumberFormat().format(v) } }
      }
    };
  }, [isDoughnut]);

  const renderChart = () => {
    if (!isDoughnut && !chartData) return <Typography className={classes.message}>No Data</Typography>;
    if (isDoughnut) {
      return (
        <div className={classes.doughnutGrid}>
          {doughnutMetrics.map(metric => {
            const percentage = metric.total > 0 ? ((metric.positive / metric.total) * 100).toFixed(1) : 0;
            return (
              <div key={metric.title} className={classes.doughnutCard}>
                <Typography className={classes.doughnutTitle}>
                  {metric.title}
                </Typography>
                <div className={classes.doughnutChart}>
                  <Doughnut data={createDoughnutChartData(metric)} options={chartOptions} />
                </div>
                <div className={classes.doughnutStats}>
                  <div className={classes.doughnutPercentage}>{percentage}%</div>
                  <div style={{ fontSize: 11, color: '#000' }}>
                    {new Intl.NumberFormat().format(metric.positive)} {metric.positiveLabel.toLowerCase()} / {new Intl.NumberFormat().format(metric.total)} {metric.totalLabel.toLowerCase()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    const ChartComponent = chartType === 'bar' ? Bar : Line;
    return (
      <div style={{ height: 340 }}>
        <ChartComponent data={chartData} options={chartOptions} />
      </div>
    );
  };

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      <div className={classes.headerBox}>
        <Typography variant="h5">Email Campaign Performance</Typography>
        <div className={classes.controls}>
          <TextField
            select
            variant="outlined"
            size="small"
            label="Chart Type"
            value={chartType}
            onChange={event => setChartType(event.target.value)}
            className={classes.chartTypeSelect}
          >
            {CHART_TYPES.map(({ value, label }) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>
        </div>
      </div>
      <Divider />
      <CardContent>
        {!isDoughnut && (
          <div className={classes.metricPanel}>
            <Typography className={classes.metricLegendLabel}>
              Metrics
            </Typography>
            <div className={classes.metricGrid}>
              {METRICS.map(metric => {
                const checked = activeMetrics.includes(metric.key);
                return (
                  <ButtonBase
                    key={metric.key}
                    onClick={() => toggleMetric(metric.key)}
                    className={clsx(classes.metricOption, checked && classes.metricOptionActive)}
                    style={{
                      borderColor: checked ? metric.color : undefined,
                      color: checked ? metric.color : undefined,
                      opacity: checked ? 1 : 0.72
                    }}>
                    <div className={classes.metricOptionLabel}>
                      <span
                        className={classes.metricState}
                        style={{
                          color: checked ? metric.color : '#94A3B8',
                          background: checked ? hexToRgba(metric.color, 0.10) : '#F8FAFC',
                          borderColor: checked ? metric.color : '#CBD5E1'
                        }}>
                        {checked ? <CheckIcon className={classes.metricStateIcon} /> : null}
                      </span>
                      <div className={classes.metricMeta}>
                        <div className={classes.metricColor} style={{ background: metric.color }} />
                        <Typography className={classes.metricText}>
                          {metric.label}
                        </Typography>
                      </div>
                    </div>
                  </ButtonBase>
                );
              })}
            </div>
          </div>
        )}
        <div className={classes.chartContainer}>
          {isLoading ? (
            <div className={classes.loader}><CircularProgress /></div>
          ) : renderChart()}
        </div>
      </CardContent>
    </Card>
  );
};

EmailCampaignPerformanceGraph.propTypes = {
  className: PropTypes.string
};

export default EmailCampaignPerformanceGraph;
